import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { BreadcrumbTrailService } from '../../../shell/breadcrumb-trail.service';

/**
 * One step in a breadcrumb trail.
 *
 * A step with no link renders as plain text, which is what a section that has
 * no page of its own (or one the current user may not open) should look like.
 */
export interface Breadcrumb {
  label: string;
  /** Router path: '/clients/list', or ['/clients/details', id]. */
  link?: string | any[];
  /** Full URL, for steps whose page is still served by twin-ci. */
  href?: string;
  /** Font Awesome 4 suffix, e.g. 'users' for fa-users. */
  icon?: string;
}

/**
 * The trail under a page title.
 *
 * Every page in the app hangs off the dashboard, so Dashboard is prepended
 * here rather than repeated by each caller; pages pass only the steps below
 * it. The last step is the page itself and is never a link, so the same trail
 * can be built without special-casing the end of it.
 *
 * Two ways to use it:
 *   <app-breadcrumbs [items]="..."></app-breadcrumbs>   the page builds its own trail
 *   <app-breadcrumbs auto></app-breadcrumbs>             built from the sidebar menu for the
 *        current URL, with the user's permissions (BreadcrumbTrailService). A page not in the
 *        menu names the menu page it hangs under and adds its own last step(s):
 *   <app-breadcrumbs auto parent="/codes-list" [tail]="[{ label: 'Import' }]"></app-breadcrumbs>
 */
@Component({
  selector: 'app-breadcrumbs',
  templateUrl: './breadcrumbs.component.html',
  styleUrls: ['./breadcrumbs.component.scss'],
})
export class BreadcrumbsComponent implements OnChanges, OnDestroy {
  @Input() items: Breadcrumb[] = [];

  /** Off for the dashboard itself, which would otherwise link to itself. */
  @Input() home = true;

  /** Build the trail from the menu instead of `items`. */
  @Input() auto: boolean | '' = false;
  /** Menu route this page hangs under (auto mode); defaults to the current URL. */
  @Input() parent: string;
  /** Steps after the menu page (auto mode), e.g. a record name. */
  @Input() tail: Breadcrumb[] = [];
  /** Auto mode: the page's own name, shown when it isn't in this user's menu at all. */
  @Input() page: string;

  private trailSub: Subscription;

  constructor(private trails: BreadcrumbTrailService, private router: Router) { }

  ngOnChanges() {
    // `auto` is a bare attribute, so it arrives as '' when set.
    if (this.auto === false || this.auto === undefined) {
      return;
    }
    if (this.trailSub) {
      this.trailSub.unsubscribe();
    }
    this.trailSub = this.trails.trail(this.parent || this.router.url, this.tail || [])
      .subscribe(items => this.items = items.length || !this.page ? items : [{ label: this.page }]);
  }

  ngOnDestroy() {
    if (this.trailSub) {
      this.trailSub.unsubscribe();
    }
  }

  /** A step is a link only when it has somewhere to go and is not the page we are on. */
  isLink(item: Breadcrumb, last: boolean): boolean {
    return !!(item.link || item.href) && !last;
  }
}
